import {
  chunkKeyInt,
  getObjectProperties,
  isCampfireLit,
  ObjectType,
  type ObjectTypeValue,
  TILE_SIZE,
  tileKeyInt,
  type WorldObjectSnapshot,
} from '@dreamgame/shared';
import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { getAllChunks } from '../../state/chunks';
import { pawns } from '../../state/entities';
import { selectedIds } from '../../state/selection';
import { COLORS, OBJECT_COLORS, OBJECT_TEXTURE_NAMES } from '../constants';
import { loadTexture, onCategoryReload } from '../TextureManager';
import {
  TREE_OBJECT_TYPES as FOREST_TREE_TYPES,
  TREE_RENDER_SCALE,
  TREE_TINTS,
  treeJitterX,
  treeJitterY,
  treeTintIndex,
} from './forestUtils';
import type { VisibleChunk } from './types';

// ============================================================================
// ObjectLayer - Renders world objects (trees, rocks, buildings)
// ============================================================================
// PERFORMANCE: Static content (walls, fallback shapes) is cached in a
// persistent Graphics object and only redrawn when visible chunks or their
// objects change. Only lightweight overlay (health bars, task highlights)
// is redrawn each frame. Sprites are managed separately in a Container.
// ============================================================================

let container: Container;
let graphicsContainer: Container;
let staticGraphics: Graphics; // Walls + fallback shapes (cached)
let campfireEffectsGraphics: Graphics; // Animated fire + smoke (per-frame)
let overlayGraphics: Graphics; // Health bars + task highlights (per-frame)

const objectTextures: Map<ObjectTypeValue, Texture> = new Map();
const doorVariantTextures: Map<string, Texture> = new Map();
let campfireOutTexture: Texture | undefined;
const objectSprites: Map<number, Sprite> = new Map();
const _usedSpriteKeys: Set<number> = new Set();

// Set of wall object types for neighbor checking
const WALL_TYPES: ReadonlySet<ObjectTypeValue> = new Set([
  ObjectType.WALL_WOOD,
  ObjectType.WALL_STONE,
  ObjectType.WALL_METAL,
]);

// Small natural props receive deterministic visual jitter/scale to avoid grid look
const GROUND_VARIATION_TYPES: ReadonlySet<ObjectTypeValue> = new Set([
  ObjectType.BUSH,
  ObjectType.ROCK_SMALL,
  ObjectType.STICKS,
  ObjectType.BRANCH,
  ObjectType.MUSHROOM_CLUSTER,
  ObjectType.WILD_FLOWERS,
  ObjectType.TREE_STUMP,
  ObjectType.FERN,
]);
const GROUND_JITTER_X = 4;
const GROUND_JITTER_Y = 3;
const GROUND_SCALE_MIN = 0.82;
const GROUND_SCALE_MAX = 1.14;

// Cached wall positions for neighbor lookup - integer keys
const _wallPositions: Set<number> = new Set();

// ============================================================================
// PERFORMANCE: Static content cache tracking
// Key: chunk integer key, Value: reference to chunk.objects array
// When visible chunks or their objects change, static content is redrawn.
// ============================================================================
const _staticCacheRefs = new Map<number, ReadonlyArray<WorldObjectSnapshot> | undefined>();

export const createObjectLayer = (): { container: Container; graphics: Container } => {
  container = new Container();
  staticGraphics = new Graphics();
  campfireEffectsGraphics = new Graphics();
  overlayGraphics = new Graphics();
  graphicsContainer = new Container();
  graphicsContainer.addChild(staticGraphics);
  graphicsContainer.addChild(campfireEffectsGraphics);
  graphicsContainer.addChild(overlayGraphics);
  return { container, graphics: graphicsContainer };
};

export const getObjectTexture = ({
  objectType,
}: {
  objectType: ObjectTypeValue;
}): Texture | undefined => {
  return objectTextures.get(objectType);
};

const getDoorVariantTextureKey = ({
  objectType,
  rotation,
}: {
  objectType: ObjectTypeValue;
  rotation: number;
}): string => {
  const isHorizontal = Math.abs(rotation % 2) === 1;
  return `${objectType}:${isHorizontal ? 'horizontal' : 'vertical'}`;
};

const getDoorVariantTextureName = ({
  objectType,
  rotation,
}: {
  objectType: ObjectTypeValue;
  rotation: number;
}): string => {
  const isHorizontal = Math.abs(rotation % 2) === 1;
  const baseName = objectType === ObjectType.DOOR_METAL ? 'door_metal' : 'door_wood';
  return `${baseName}_${isHorizontal ? 'horizontal' : 'vertical'}`;
};

export const loadObjectTextures = async (): Promise<void> => {
  try {
    for (const [objectType, name] of Object.entries(OBJECT_TEXTURE_NAMES)) {
      try {
        const texture = await loadTexture({ path: `/objects/${name}.png` });
        objectTextures.set(Number(objectType) as ObjectTypeValue, texture);
      } catch {
        console.warn(`Object texture not found: /objects/${name}.png`);
      }
    }

    const doorTypes: ReadonlyArray<ObjectTypeValue> = [ObjectType.DOOR_WOOD, ObjectType.DOOR_METAL];
    for (const objectType of doorTypes) {
      for (const rotation of [0, 1] as const) {
        const textureName = getDoorVariantTextureName({ objectType, rotation });
        try {
          const texture = await loadTexture({ path: `/objects/${textureName}.png` });
          doorVariantTextures.set(getDoorVariantTextureKey({ objectType, rotation }), texture);
        } catch {
          // Optional variants: fall back to base texture when missing.
        }
      }
    }

    try {
      campfireOutTexture = await loadTexture({ path: '/objects/campfire_out.png' });
    } catch {
      campfireOutTexture = undefined;
    }

    console.log(`Loaded ${objectTextures.size} object textures`);
  } catch (error) {
    console.warn('Failed to load object textures, using fallback colors', error);
  }
};

// Reload textures (clears sprites and reloads from TextureManager)
export const reloadObjectTextures = async (): Promise<void> => {
  for (const sprite of objectSprites.values()) {
    sprite.destroy();
  }
  objectSprites.clear();
  objectTextures.clear();
  doorVariantTextures.clear();
  campfireOutTexture = undefined;
  // Invalidate static cache so walls/fallbacks rebuild with new textures
  _staticCacheRefs.clear();
  await loadObjectTextures();
  console.log('Object textures reloaded');
};

// Register for texture reload events
onCategoryReload({ category: 'objects', callback: reloadObjectTextures });

// PERFORMANCE: Preallocated Set, cleared each frame instead of new Set()
const _taskTargets: Set<number> = new Set();

// Collect task targets from selected pawns
const collectTaskTargets = (): Set<number> => {
  _taskTargets.clear();
  const selectedPawnIds = selectedIds.value;
  const allPawns = pawns.value;

  for (const pawnId of selectedPawnIds) {
    const pawn = allPawns.get(pawnId);
    if (pawn?.task?.targetEntity) {
      _taskTargets.add(pawn.task.targetEntity);
    }
  }
  return _taskTargets;
};

// PERFORMANCE: Preallocated array, reset with .length = 0 each frame
const _allObjects: Array<WorldObjectSnapshot> = [];

// Campfire effect constants
const CAMPFIRE_FLAME_SWAY_SPEED = 4.4;
const CAMPFIRE_FLAME_FLICKER_SPEED = 7.6;
const CAMPFIRE_FLAME_DETAIL_SPEED = 13.2;
const CAMPFIRE_SMOKE_PUFFS = 12;
const CAMPFIRE_SMOKE_RISE_PIXELS = 44;
const CAMPFIRE_SMOKE_DRIFT_MIN = 0.45;
const CAMPFIRE_SMOKE_DRIFT_MAX = 5.4;
const CAMPFIRE_SMOKE_RADIUS_MIN = 2.1;
const CAMPFIRE_SMOKE_RADIUS_MAX = 8.2;
const CAMPFIRE_SMOKE_BASE_ALPHA = 0.22;
const _campfirePhaseParams = { tileX: 0, tileY: 0 };
const _campfireSeedParams = { tileX: 0, tileY: 0, seed: 0 };
const _campfireDrawParams = { timeSeconds: 0 };

const campfirePhase = ({ tileX, tileY }: { tileX: number; tileY: number }): number => {
  // Deterministic per-tile phase so campfires do not animate in lockstep.
  const hash = ((tileX * 73856093) ^ (tileY * 19349663)) >>> 0;
  return (hash % 2048) / 2048;
};

const campfireSeed = ({
  tileX,
  tileY,
  seed,
}: {
  tileX: number;
  tileY: number;
  seed: number;
}): number => {
  const hash = ((tileX * 73856093) ^ (tileY * 19349663) ^ (seed * 83492791)) >>> 0;
  return (hash % 2048) / 2048;
};

const drawCampfireEffects = ({ timeSeconds }: { timeSeconds: number }): void => {
  // PERFORMANCE: Draw directly from _allObjects and avoid per-frame arrays/objects.
  for (const obj of _allObjects) {
    if (obj.objectType !== ObjectType.CAMPFIRE || !isCampfireLit({ state: obj.state ?? 0 })) {
      continue;
    }

    const worldX = obj.tileX * TILE_SIZE;
    const worldY = obj.tileY * TILE_SIZE;
    const baseX = worldX + TILE_SIZE * 0.5;
    const baseY = worldY + TILE_SIZE * 0.66;
    _campfirePhaseParams.tileX = obj.tileX;
    _campfirePhaseParams.tileY = obj.tileY;
    const phase = campfirePhase(_campfirePhaseParams);
    const phaseRadians = phase * Math.PI * 2;
    const flameSway =
      Math.sin(timeSeconds * CAMPFIRE_FLAME_SWAY_SPEED + phaseRadians) * 0.74 +
      Math.sin(timeSeconds * CAMPFIRE_FLAME_SWAY_SPEED * 1.7 + phaseRadians * 1.8) * 0.32;
    const flameFlicker =
      Math.sin(timeSeconds * CAMPFIRE_FLAME_FLICKER_SPEED + phaseRadians * 0.9) * 0.22 +
      Math.sin(timeSeconds * CAMPFIRE_FLAME_DETAIL_SPEED + phaseRadians * 1.6) * 0.1;
    const flameBaseY = baseY - 1.4;

    for (let puffIndex = 0; puffIndex < CAMPFIRE_SMOKE_PUFFS; puffIndex++) {
      _campfireSeedParams.tileX = obj.tileX;
      _campfireSeedParams.tileY = obj.tileY;
      _campfireSeedParams.seed = 101 + puffIndex * 31;
      const puffPhase = campfireSeed(_campfireSeedParams);
      _campfireSeedParams.seed = 151 + puffIndex * 29;
      const puffRateSeed = campfireSeed(_campfireSeedParams);
      _campfireSeedParams.seed = 191 + puffIndex * 23;
      const puffDriftSeed = campfireSeed(_campfireSeedParams);

      const puffSpeed = 0.14 + puffRateSeed * 0.07;
      const progressRaw = timeSeconds * puffSpeed + phase * 2.1 + puffPhase;
      const progress = progressRaw - Math.floor(progressRaw);
      const rise = progress * CAMPFIRE_SMOKE_RISE_PIXELS + puffRateSeed * 4;
      const driftAmplitude =
        CAMPFIRE_SMOKE_DRIFT_MIN +
        puffDriftSeed * (CAMPFIRE_SMOKE_DRIFT_MAX - CAMPFIRE_SMOKE_DRIFT_MIN);
      const drift =
        (puffDriftSeed - 0.5) * 2.1 +
        Math.sin(timeSeconds * (0.85 + puffDriftSeed * 0.45) + phaseRadians + puffIndex * 1.23) *
          driftAmplitude;
      const radius =
        CAMPFIRE_SMOKE_RADIUS_MIN +
        puffRateSeed * (CAMPFIRE_SMOKE_RADIUS_MAX - CAMPFIRE_SMOKE_RADIUS_MIN);
      const alpha = CAMPFIRE_SMOKE_BASE_ALPHA - progress * 0.08;
      const smokeX = baseX + drift;
      const smokeY = baseY - 9 - rise;

      campfireEffectsGraphics.setFillStyle({ color: 0x575250, alpha: alpha * 0.45 });
      campfireEffectsGraphics.circle(smokeX, smokeY, radius * 1.35);
      campfireEffectsGraphics.fill();

      campfireEffectsGraphics.setFillStyle({ color: 0x6f6a67, alpha });
      campfireEffectsGraphics.circle(smokeX, smokeY, radius);
      campfireEffectsGraphics.fill();
    }

    campfireEffectsGraphics.setFillStyle({ color: 0xff8f38, alpha: 0.14 });
    campfireEffectsGraphics.ellipse(baseX, baseY + 1.2, 8.4, 3.9);
    campfireEffectsGraphics.fill();

    campfireEffectsGraphics.setFillStyle({ color: 0xff651c, alpha: 0.5 });
    campfireEffectsGraphics.ellipse(
      baseX + flameSway * 0.34,
      flameBaseY - (4.2 + flameFlicker * 0.6),
      4.8 + flameFlicker * 0.35,
      6.4 + flameFlicker * 0.75
    );
    campfireEffectsGraphics.fill();

    campfireEffectsGraphics.setFillStyle({ color: 0xff982f, alpha: 0.67 });
    campfireEffectsGraphics.ellipse(
      baseX - 1.45 + flameSway * 0.25,
      flameBaseY - (5.4 + flameFlicker * 0.45),
      2.15,
      4.85 + flameFlicker * 0.6
    );
    campfireEffectsGraphics.fill();
    campfireEffectsGraphics.ellipse(
      baseX + 1.55 + flameSway * 0.18,
      flameBaseY - (5.25 + flameFlicker * 0.4),
      1.95,
      4.7 + flameFlicker * 0.5
    );
    campfireEffectsGraphics.fill();
    campfireEffectsGraphics.ellipse(
      baseX + flameSway * 0.28,
      flameBaseY - (6.3 + flameFlicker * 0.55),
      2.35,
      5.8 + flameFlicker * 0.7
    );
    campfireEffectsGraphics.fill();

    campfireEffectsGraphics.setFillStyle({ color: 0xffe7a4, alpha: 0.82 });
    campfireEffectsGraphics.ellipse(
      baseX + flameSway * 0.17,
      flameBaseY - (5.7 + flameFlicker * 0.42),
      1.55,
      3.6 + flameFlicker * 0.45
    );
    campfireEffectsGraphics.fill();
  }
};

// Collect all visible objects and wall positions
const collectVisibleObjects = ({
  visibleChunks,
  loadedChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
  loadedChunks: ReturnType<typeof getAllChunks>;
}): void => {
  _allObjects.length = 0;
  _wallPositions.clear();

  for (const { x: chunkX, y: chunkY } of visibleChunks) {
    const chunkKey = chunkKeyInt({ x: chunkX, y: chunkY });
    const chunk = loadedChunks.get(chunkKey);

    if (!chunk?.objects) {
      continue;
    }

    for (const obj of chunk.objects) {
      _allObjects.push(obj);
      const objectType = obj.objectType as ObjectTypeValue;
      if (WALL_TYPES.has(objectType)) {
        _wallPositions.add(tileKeyInt({ x: obj.tileX, y: obj.tileY }));
      }
    }
  }
};

// Check if static content needs redraw
const isStaticCacheDirty = ({
  visibleChunks,
  loadedChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
  loadedChunks: ReturnType<typeof getAllChunks>;
}): boolean => {
  if (visibleChunks.length !== _staticCacheRefs.size) {
    return true;
  }
  for (const { x, y } of visibleChunks) {
    const key = chunkKeyInt({ x, y });
    const chunk = loadedChunks.get(key);
    if (_staticCacheRefs.get(key) !== chunk?.objects) {
      return true;
    }
  }
  return false;
};

const updateStaticCacheRefs = ({
  visibleChunks,
  loadedChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
  loadedChunks: ReturnType<typeof getAllChunks>;
}): void => {
  _staticCacheRefs.clear();
  for (const { x, y } of visibleChunks) {
    const key = chunkKeyInt({ x, y });
    const chunk = loadedChunks.get(key);
    _staticCacheRefs.set(key, chunk?.objects);
  }
};

// ============================================================================
// Static content rendering (walls + fallbacks + sprites, only on rebuild)
// ============================================================================

// Apply tree tint to a sprite based on its tile position
const applyTreeTint = ({
  sprite,
  tileX,
  tileY,
}: {
  sprite: Sprite;
  tileX: number;
  tileY: number;
}): void => {
  const tint = TREE_TINTS[treeTintIndex({ tileX, tileY })];
  if (tint !== undefined) {
    sprite.tint = tint;
  }
};

// Compute sprite dimensions and position for an object (trees get oversized rendering)
// PERFORMANCE: Preallocated result object, caller must use values immediately
const _spriteLayout = { width: 0, height: 0, x: 0, y: 0 };
const groundVariationHash = ({
  tileX,
  tileY,
  seed,
}: {
  tileX: number;
  tileY: number;
  seed: number;
}): number => {
  return (((tileX * 2654435761 + seed * 1597334677) ^ (tileY * 2246822519)) >>> 0) % 1000;
};

const computeSpriteLayout = ({
  obj,
  objProps,
  isTree,
}: {
  obj: WorldObjectSnapshot;
  objProps: { width: number; height: number };
  isTree: boolean;
}): typeof _spriteLayout => {
  const worldX = obj.tileX * TILE_SIZE;
  const worldY = obj.tileY * TILE_SIZE;
  const baseWidth = objProps.width * TILE_SIZE;
  const baseHeight = objProps.height * TILE_SIZE;

  if (isTree) {
    const w = baseWidth * TREE_RENDER_SCALE;
    const h = baseHeight * TREE_RENDER_SCALE;
    const jx = treeJitterX({ tileX: obj.tileX, tileY: obj.tileY });
    const jy = treeJitterY({ tileX: obj.tileX, tileY: obj.tileY });
    _spriteLayout.width = w;
    _spriteLayout.height = h;
    _spriteLayout.x = worldX - (w - baseWidth) / 2 + jx;
    _spriteLayout.y = worldY + baseHeight - h + jy;
  } else {
    const objectType = obj.objectType as ObjectTypeValue;
    const canVaryGroundSprite =
      objProps.width === 1 && objProps.height === 1 && GROUND_VARIATION_TYPES.has(objectType);

    if (canVaryGroundSprite) {
      const jx =
        (groundVariationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 17 }) %
          (GROUND_JITTER_X * 2 + 1)) -
        GROUND_JITTER_X;
      const jy =
        (groundVariationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 23 }) %
          (GROUND_JITTER_Y * 2 + 1)) -
        GROUND_JITTER_Y;
      const scaleNoise =
        groundVariationHash({ tileX: obj.tileX, tileY: obj.tileY, seed: 31 }) / 1000;
      const scale = GROUND_SCALE_MIN + scaleNoise * (GROUND_SCALE_MAX - GROUND_SCALE_MIN);
      const variedWidth = baseWidth * scale;
      const variedHeight = baseHeight * scale;

      _spriteLayout.width = variedWidth;
      _spriteLayout.height = variedHeight;
      _spriteLayout.x = worldX + (baseWidth - variedWidth) / 2 + jx;
      _spriteLayout.y = worldY + (baseHeight - variedHeight) + jy;
    } else {
      _spriteLayout.width = baseWidth;
      _spriteLayout.height = baseHeight;
      _spriteLayout.x = worldX;
      _spriteLayout.y = worldY;
    }
  }
  return _spriteLayout;
};

// Place or update a textured sprite for an object
const placeObjectSprite = ({
  obj,
  texture,
  isTree,
}: {
  obj: WorldObjectSnapshot;
  texture: Texture;
  isTree: boolean;
}): void => {
  const objProps = getObjectProperties({ objectType: obj.objectType as ObjectTypeValue });
  const layout = computeSpriteLayout({ obj, objProps, isTree });
  const spriteKey = tileKeyInt({ x: obj.tileX, y: obj.tileY });
  _usedSpriteKeys.add(spriteKey);
  const isDoor = DOOR_TYPES.has(obj.objectType as ObjectTypeValue);
  const doorOpenAlpha = (obj.state ?? 0) === 1 ? 0.65 : 1;

  const existingSprite = objectSprites.get(spriteKey);
  if (existingSprite) {
    if (existingSprite.texture !== texture) {
      existingSprite.texture = texture;
    }
    existingSprite.x = layout.x;
    existingSprite.y = layout.y;
    existingSprite.width = layout.width;
    existingSprite.height = layout.height;
    existingSprite.visible = true;
    existingSprite.alpha = isDoor ? doorOpenAlpha : 1;
    if (isTree) {
      applyTreeTint({ sprite: existingSprite, tileX: obj.tileX, tileY: obj.tileY });
    }
  } else {
    const sprite = new Sprite(texture);
    sprite.width = layout.width;
    sprite.height = layout.height;
    sprite.x = layout.x;
    sprite.y = layout.y;
    sprite.alpha = isDoor ? doorOpenAlpha : 1;
    if (isTree) {
      applyTreeTint({ sprite, tileX: obj.tileX, tileY: obj.tileY });
    }
    container.addChild(sprite);
    objectSprites.set(spriteKey, sprite);
  }
};

const resolveObjectTexture = ({
  obj,
  objectType,
}: {
  obj: WorldObjectSnapshot;
  objectType: ObjectTypeValue;
}): Texture | undefined => {
  if (DOOR_TYPES.has(objectType)) {
    return (
      doorVariantTextures.get(
        getDoorVariantTextureKey({ objectType, rotation: obj.rotation ?? 0 })
      ) ?? objectTextures.get(objectType)
    );
  }

  if (objectType === ObjectType.CAMPFIRE && !isCampfireLit({ state: obj.state ?? 0 })) {
    return campfireOutTexture ?? objectTextures.get(objectType);
  }

  return objectTextures.get(objectType);
};

// Draw a single object's static visuals (wall, sprite, or fallback shape)
const drawObjectVisuals = ({ obj }: { obj: WorldObjectSnapshot }): void => {
  const objectType = obj.objectType as ObjectTypeValue;

  if (WALL_TYPES.has(objectType)) {
    const colors = OBJECT_COLORS[objectType];
    if (colors) {
      drawWallWithNeighbors({ tileX: obj.tileX, tileY: obj.tileY, colors });
    }
    return;
  }

  const texture = resolveObjectTexture({ obj, objectType });
  const isTree = FOREST_TREE_TYPES.has(objectType);

  if (texture) {
    placeObjectSprite({ obj, texture, isTree });
  } else {
    const worldX = obj.tileX * TILE_SIZE;
    const worldY = obj.tileY * TILE_SIZE;
    const colors = OBJECT_COLORS[objectType];
    if (colors) {
      drawObjectFallback({ worldX, worldY, objectType, colors, state: obj.state ?? 0 });
    }
  }
};

// ============================================================================
// Overlay rendering (health bars + task highlights, every frame)
// ============================================================================

const drawObjectOverlay = ({
  obj,
  taskTargets,
}: {
  obj: WorldObjectSnapshot;
  taskTargets: Set<number>;
}): void => {
  const worldX = obj.tileX * TILE_SIZE;
  const worldY = obj.tileY * TILE_SIZE;
  const objectType = obj.objectType as ObjectTypeValue;
  const objProps = getObjectProperties({ objectType });
  const isTaskTargeted = taskTargets.has(obj.networkId);
  const isTreeOrBush = FOREST_TREE_TYPES.has(objectType) || objectType === ObjectType.BUSH;

  if (isTaskTargeted) {
    drawTaskTargetHighlight({ worldX, worldY, width: objProps.width, height: objProps.height });
  }

  const shouldShowHealthBar = obj.health < obj.maxHealth && (!isTreeOrBush || isTaskTargeted);

  if (shouldShowHealthBar) {
    drawObjectHealthBar({
      worldX,
      worldY,
      health: obj.health,
      maxHealth: obj.maxHealth,
      width: objProps.width,
    });
  }
};

// Hide sprites no longer visible
const hideUnusedSprites = (): void => {
  for (const [key, sprite] of objectSprites) {
    if (!_usedSpriteKeys.has(key)) {
      sprite.visible = false;
    }
  }
};

// ============================================================================
// Main draw entry point
// ============================================================================

export const drawObjects = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  campfireEffectsGraphics.clear();
  overlayGraphics.clear();
  const taskTargets = collectTaskTargets();
  const loadedChunks = getAllChunks();

  // PERFORMANCE: Only redraw static content (walls, fallbacks, sprites)
  // when visible chunks or their objects change. During smooth panning
  // within the same chunk range, this skips all expensive Graphics draws.
  if (isStaticCacheDirty({ visibleChunks, loadedChunks })) {
    staticGraphics.clear();
    _usedSpriteKeys.clear();
    collectVisibleObjects({ visibleChunks, loadedChunks });

    for (const obj of _allObjects) {
      drawObjectVisuals({ obj });
    }
    hideUnusedSprites();
    updateStaticCacheRefs({ visibleChunks, loadedChunks });
  }

  // Overlay (health bars + task highlights) redrawn every frame.
  // _allObjects persists from last rebuild; object references reflect
  // any in-place health updates from the server.
  _campfireDrawParams.timeSeconds = performance.now() * 0.001;
  drawCampfireEffects(_campfireDrawParams);

  for (const obj of _allObjects) {
    drawObjectOverlay({ obj, taskTargets });
  }
};

// ============================================================================
// Wall rendering (into staticGraphics)
// ============================================================================

const drawWallWithNeighbors = ({
  tileX,
  tileY,
  colors,
}: {
  tileX: number;
  tileY: number;
  colors: { fill: number; stroke: number };
}): void => {
  const worldX = tileX * TILE_SIZE;
  const worldY = tileY * TILE_SIZE;

  // Check neighbors (using integer keys for performance)
  const hasTop = _wallPositions.has(tileKeyInt({ x: tileX, y: tileY - 1 }));
  const hasBottom = _wallPositions.has(tileKeyInt({ x: tileX, y: tileY + 1 }));
  const hasLeft = _wallPositions.has(tileKeyInt({ x: tileX - 1, y: tileY }));
  const hasRight = _wallPositions.has(tileKeyInt({ x: tileX + 1, y: tileY }));

  // Fill the entire tile
  staticGraphics.setFillStyle({ color: colors.fill });
  staticGraphics.rect(worldX, worldY, TILE_SIZE, TILE_SIZE);
  staticGraphics.fill();

  // Draw borders only on exposed sides (no neighbor)
  const borderWidth = 2;
  staticGraphics.setStrokeStyle({ width: borderWidth, color: colors.stroke });

  if (!hasTop) {
    staticGraphics.moveTo(worldX, worldY + borderWidth / 2);
    staticGraphics.lineTo(worldX + TILE_SIZE, worldY + borderWidth / 2);
    staticGraphics.stroke();
  }
  if (!hasBottom) {
    staticGraphics.moveTo(worldX, worldY + TILE_SIZE - borderWidth / 2);
    staticGraphics.lineTo(worldX + TILE_SIZE, worldY + TILE_SIZE - borderWidth / 2);
    staticGraphics.stroke();
  }
  if (!hasLeft) {
    staticGraphics.moveTo(worldX + borderWidth / 2, worldY);
    staticGraphics.lineTo(worldX + borderWidth / 2, worldY + TILE_SIZE);
    staticGraphics.stroke();
  }
  if (!hasRight) {
    staticGraphics.moveTo(worldX + TILE_SIZE - borderWidth / 2, worldY);
    staticGraphics.lineTo(worldX + TILE_SIZE - borderWidth / 2, worldY + TILE_SIZE);
    staticGraphics.stroke();
  }

  // Add subtle inner detail/texture
  staticGraphics.setFillStyle({ color: colors.stroke, alpha: 0.15 });

  // Horizontal wood grain effect
  for (let i = 1; i < 4; i++) {
    const y = worldY + (TILE_SIZE / 4) * i;
    staticGraphics.rect(worldX + 4, y - 0.5, TILE_SIZE - 8, 1);
    staticGraphics.fill();
  }
};

// ============================================================================
// Fallback shape rendering (into staticGraphics)
// ============================================================================

// Object type categories for rendering
const TREE_TYPES: ReadonlySet<ObjectTypeValue> = new Set([
  ObjectType.TREE_OAK,
  ObjectType.TREE_PINE,
  ObjectType.TREE_BIRCH,
]);

const ROCK_TYPES: ReadonlySet<ObjectTypeValue> = new Set([
  ObjectType.ROCK_SMALL,
  ObjectType.ROCK_MEDIUM,
  ObjectType.ROCK_LARGE,
  ObjectType.BOULDER,
]);

const DOOR_TYPES: ReadonlySet<ObjectTypeValue> = new Set([
  ObjectType.DOOR_WOOD,
  ObjectType.DOOR_METAL,
]);

const ORE_TYPES: ReadonlySet<ObjectTypeValue> = new Set([
  ObjectType.ORE_IRON,
  ObjectType.ORE_GOLD,
  ObjectType.ORE_COPPER,
  ObjectType.ORE_TIN,
  ObjectType.ORE_COAL,
  ObjectType.ORE_SILVER,
  ObjectType.ORE_MITHRIL,
  ObjectType.ORE_STARMETAL,
  ObjectType.ORE_TIN_LARGE,
  ObjectType.ORE_COPPER_LARGE,
  ObjectType.ORE_IRON_LARGE,
  ObjectType.ORE_COAL_LARGE,
  ObjectType.ORE_SILVER_LARGE,
  ObjectType.ORE_GOLD_LARGE,
  ObjectType.ORE_MITHRIL_LARGE,
  ObjectType.ORE_STARMETAL_LARGE,
]);

const drawTreeFallback = ({
  worldX,
  worldY,
  height,
  colors,
}: {
  worldX: number;
  worldY: number;
  height: number;
  colors: { fill: number; stroke: number };
}): void => {
  const totalHeight = height * TILE_SIZE;
  const centerX = worldX + TILE_SIZE / 2;

  // Trunk in bottom portion
  const trunkHeight = totalHeight * 0.4;
  const trunkTop = worldY + totalHeight - trunkHeight - 2;
  staticGraphics.setFillStyle({ color: colors.stroke });
  staticGraphics.rect(centerX - 3, trunkTop, 6, trunkHeight);
  staticGraphics.fill();

  // Canopy circle in upper portion
  const canopyCenterY = worldY + totalHeight * 0.35;
  const canopyRadius = TILE_SIZE * 0.45;
  staticGraphics.setFillStyle({ color: colors.fill });
  staticGraphics.circle(centerX, canopyCenterY, canopyRadius);
  staticGraphics.fill();
};

const drawBushFallback = ({
  worldX,
  worldY,
  size,
  colors,
}: {
  worldX: number;
  worldY: number;
  size: number;
  colors: { fill: number; stroke: number };
}): void => {
  const centerX = worldX + TILE_SIZE / 2;
  const centerY = worldY + TILE_SIZE / 2;
  staticGraphics.setFillStyle({ color: colors.fill });
  staticGraphics.circle(centerX, centerY, size / 3);
  staticGraphics.fill();
};

const drawRockFallback = ({
  worldX,
  worldY,
  padding,
  size,
  colors,
}: {
  worldX: number;
  worldY: number;
  padding: number;
  size: number;
  colors: { fill: number; stroke: number };
}): void => {
  staticGraphics.setFillStyle({ color: colors.fill });
  staticGraphics.roundRect(worldX + padding, worldY + padding, size, size, 6);
  staticGraphics.fill();
  staticGraphics.setStrokeStyle({ width: 2, color: colors.stroke });
  staticGraphics.roundRect(worldX + padding, worldY + padding, size, size, 6);
  staticGraphics.stroke();
};

const drawDoorFallback = ({
  worldX,
  worldY,
  colors,
  state,
}: {
  worldX: number;
  worldY: number;
  colors: { fill: number; stroke: number };
  state: number;
}): void => {
  const isOpen = state === 1;
  const inset = isOpen ? 10 : 6;
  const alpha = isOpen ? 0.6 : 1;

  staticGraphics.setFillStyle({ color: colors.fill });
  staticGraphics.rect(worldX + inset, worldY + 2, TILE_SIZE - inset * 2, TILE_SIZE - 4);
  staticGraphics.fill();
  staticGraphics.setStrokeStyle({ width: 2, color: colors.stroke, alpha });
  staticGraphics.rect(worldX + inset, worldY + 2, TILE_SIZE - inset * 2, TILE_SIZE - 4);
  staticGraphics.stroke();
};

const drawOreFallback = ({
  worldX,
  worldY,
  padding,
  size,
  colors,
}: {
  worldX: number;
  worldY: number;
  padding: number;
  size: number;
  colors: { fill: number; stroke: number };
}): void => {
  staticGraphics.setFillStyle({ color: colors.fill });
  staticGraphics.roundRect(worldX + padding, worldY + padding, size, size, 4);
  staticGraphics.fill();
  staticGraphics.setFillStyle({ color: colors.stroke });
  staticGraphics.circle(worldX + TILE_SIZE / 2 - 4, worldY + TILE_SIZE / 2, 3);
  staticGraphics.fill();
  staticGraphics.circle(worldX + TILE_SIZE / 2 + 4, worldY + TILE_SIZE / 2 - 4, 2);
  staticGraphics.fill();
};

const drawDefaultFallback = ({
  worldX,
  worldY,
  padding,
  sizeWidth,
  sizeHeight,
  colors,
}: {
  worldX: number;
  worldY: number;
  padding: number;
  sizeWidth: number;
  sizeHeight: number;
  colors: { fill: number; stroke: number };
}): void => {
  staticGraphics.setFillStyle({ color: colors.fill });
  staticGraphics.rect(worldX + padding, worldY + padding, sizeWidth, sizeHeight);
  staticGraphics.fill();
};

const drawObjectFallback = ({
  worldX,
  worldY,
  objectType,
  colors,
  state,
}: {
  worldX: number;
  worldY: number;
  objectType: ObjectTypeValue;
  colors: { fill: number; stroke: number };
  state: number;
}): void => {
  const objProps = getObjectProperties({ objectType });
  const padding = 4;
  const sizeWidth = objProps.width * TILE_SIZE - padding * 2;
  const sizeHeight = objProps.height * TILE_SIZE - padding * 2;
  const size = TILE_SIZE - padding * 2; // For single-tile fallbacks

  if (TREE_TYPES.has(objectType)) {
    drawTreeFallback({ worldX, worldY, height: objProps.height, colors });
  } else if (objectType === ObjectType.BUSH) {
    drawBushFallback({ worldX, worldY, size, colors });
  } else if (ROCK_TYPES.has(objectType)) {
    drawRockFallback({ worldX, worldY, padding, size, colors });
  } else if (DOOR_TYPES.has(objectType)) {
    drawDoorFallback({ worldX, worldY, colors, state });
  } else if (ORE_TYPES.has(objectType)) {
    drawOreFallback({ worldX, worldY, padding, size, colors });
  } else {
    drawDefaultFallback({ worldX, worldY, padding, sizeWidth, sizeHeight, colors });
  }
};

// ============================================================================
// Overlay drawing functions (into overlayGraphics)
// ============================================================================

// Draw task target highlight
const drawTaskTargetHighlight = ({
  worldX,
  worldY,
  width,
  height,
}: {
  worldX: number;
  worldY: number;
  width: number;
  height: number;
}): void => {
  overlayGraphics.setStrokeStyle({ width: 3, color: COLORS.TASK_TARGET_STROKE });
  overlayGraphics.roundRect(
    worldX + 1,
    worldY + 1,
    width * TILE_SIZE - 2,
    height * TILE_SIZE - 2,
    6
  );
  overlayGraphics.stroke();
};

const drawObjectHealthBar = ({
  worldX,
  worldY,
  health,
  maxHealth,
  width = 1,
}: {
  worldX: number;
  worldY: number;
  health: number;
  maxHealth: number;
  width?: number;
}): void => {
  const objectWidth = width * TILE_SIZE;
  const barWidth = objectWidth - 8;
  const barHeight = 3;
  const barX = worldX + 4;
  const barY = worldY - 6;
  const healthPercent = health / maxHealth;

  overlayGraphics.setFillStyle({ color: 0x222222 });
  overlayGraphics.rect(barX, barY, barWidth, barHeight);
  overlayGraphics.fill();

  const healthColor = healthPercent > 0.6 ? 0x44cc44 : healthPercent > 0.3 ? 0xcccc44 : 0xcc4444;
  overlayGraphics.setFillStyle({ color: healthColor });
  overlayGraphics.rect(barX, barY, barWidth * healthPercent, barHeight);
  overlayGraphics.fill();
};
